import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./ProteinCalculator.module.scss";
import { getPatientTypes, getRecommendedProtein, getSupplementProtein } from "../../../../../app/homeSlice";
import { VIEW } from "../constant";
import { PatientInfoForm } from "./PatientInfoForm";
import { RecommendedProteinView } from "./RecommendedProteinView";
import { FinalSummaryView } from "./FinalSummaryView";
import { useLoading } from "../../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";

export const ProteinCalculator = ({ view, setView }) => {
  const dispatch = useDispatch();
  const patientTypes = useSelector((s) => s.home.patientTypes);

  const recommendedLoading = useLoading(LOADING_KEYS.GET_RECOMMENDED_PROTEIN);
  const supplementLoading = useLoading(LOADING_KEYS.GET_SUPPLEMENT_PROTEIN);

  const [formData, setFormData] = useState({ weight: "", patientType: "" });
  const [formErrors, setFormErrors] = useState({});

  const [recommended, setRecommended] = useState(null);
  const [dailyProteinFood, setDailyProteinFood] = useState("");
  const [supplementErrors, setSupplementErrors] = useState({});
  const [supplementMessage, setSupplementMessage] = useState(null);
  const [supplementResult, setSupplementResult] = useState(null);

  useEffect(() => {
    dispatch(getPatientTypes());
  }, [dispatch]);

  const patientTypeLabel = useMemo(
    () => patientTypes?.find((pt) => String(pt.key) === String(formData.patientType))?.value || "",
    [patientTypes, formData.patientType]
  );

  useEffect(() => {
    if (!supplementMessage) return;
    const timer = setTimeout(() => {
      setSupplementMessage(null);
    }, 5000);

    return () => clearTimeout(timer);
  }, [supplementMessage]);

  const handleCalculateRecommended = useCallback(async () => {
    const errors = {};
    if (!formData.weight) errors.weight = "Weight is required";
    if (!formData.patientType) errors.patientType = "Type of patient is required";
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    const result = await dispatch(getRecommendedProtein({ weight: formData.weight, patientType: formData.patientType }));
    if (result) {
      setRecommended(result);
      setView(VIEW.REC_PROTEIN);
    }
  }, [dispatch, formData, setView]);

  const handleCalculateSupplement = useCallback(async () => {
    const errors = {};
    if (!dailyProteinFood) errors.dailyProteinFood = "This field is required";
    setSupplementErrors(errors);
    if (Object.keys(errors).length) return;

    setSupplementMessage(null);
    const result = await dispatch(
      getSupplementProtein({ weight: formData.weight, patientType: formData.patientType, dailyProtein: dailyProteinFood })
    );
    if (result?.type === "next" && result?.response) {
      setSupplementResult(result?.response);
      setView(VIEW.PROTEIN_FINAL_VIEW);
    } else if (result?.type === "current") {
      setSupplementMessage(result?.response);
    }
  }, [dispatch, formData, dailyProteinFood, setView]);

//   useEffect(() => {
//     if (apiError) setSupplementMessage(apiError);
//   }, [apiError]);

  return (
    <div className={styles.container}>
      {view === VIEW.PROTEIN_CALCULATOR && (
        <PatientInfoForm
          formData={formData}
          setFormData={setFormData}
          errors={formErrors}
          patientTypes={patientTypes || []}
          loading={recommendedLoading}
          onCalculate={handleCalculateRecommended}
        />
      )}

      {view === VIEW.REC_PROTEIN && recommended && (
        <RecommendedProteinView
          recommended={recommended}
          dailyProteinFood={dailyProteinFood}
          setDailyProteinFood={setDailyProteinFood}
          errors={supplementErrors}
          loading={supplementLoading}
          message={supplementMessage}
          onCalculate={handleCalculateSupplement}
        />
      )}

      {view === VIEW.PROTEIN_FINAL_VIEW && supplementResult && (
        <FinalSummaryView
          weight={formData.weight}
          patientTypeLabel={patientTypeLabel}
          recommended={recommended}
          dailyProteinFood={dailyProteinFood}
          supplementResult={supplementResult}
        />
      )}
    </div>
  );
};
